import { TelemetryEntity } from "@/lib/telemetry";
import { useViewportInfoContext } from "@/src/contexts";
import type {
    IDiscountsPanel,
    IDiscountsPanelResult,
    IDiscountsPanelViewState,
} from "@/src/discount/DiscountsPanel/DiscountsPanel";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { jumpToSectionElement } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";
import type { IPanelTitleElement } from "@Components/Panel";
import { Panel } from "@Components/Panel";

import DiscountsPanel from "../discount/DiscountsPanel";
import type { ICategoriesCarousel, ICategoriesCarouselItem } from "./CategoriesCarousel";
import { CategoriesCarousel } from "./CategoriesCarousel";
import type { IComponentViewState } from "./ComponentViewState";
import type { IContentContainer } from "./ContentContainer";
import ContentContainer from "./ContentContainer";

export interface ILatestOffersResult extends ILatestOffersData {
    isPersonalized: boolean;
}

interface ILatestOffersData {
    categoriesCarousel?: ICategoriesCarousel;
    panels: IDiscountsPanelExtension[];
}

export interface ILatestOffers
    extends IComponentAjax<ILatestOffersResult>, IComponentViewState<ILatestOffersViewState> {
    categoriesPanelTitle: string;
    moreCategoriesPanelTitle: string;
    contentContainers?: IContentContainer;
    panelTitleElement?: keyof IPanelTitleElement;
    device: Device;
}

interface IDiscountsPanelExtension extends IDiscountsPanel {
    hide?: boolean;
}

interface ILatestOffersState extends IComponentAjaxState {
    result: ILatestOffersData;
    isPersonalized: boolean;
}

export interface ILatestOffersViewState {
    activePanelState: IDiscountsPanelViewState;
}

class LatestOffers extends ComponentAjax<ILatestOffersResult, ILatestOffers, ILatestOffersState> {
    private get panels(): IDiscountsPanelExtension[] {
        return this.state.result?.panels || [];
    }

    private set panels(panels: IDiscountsPanelExtension[]) {
        const result = { ...this.state.result };
        result.panels = panels;

        this.setState(() => ({ result: { ...result } }));
    }

    private categoriesCounter: number = 0;

    constructor(props: ILatestOffers) {
        super(props);

        this.state = {
            isLoading:
                (!props.initialData?.panels || props.initialData.panels.length === 0) &&
                props.ajaxAction !== null,
            isPersonalized: (props.initialData && props.initialData.isPersonalized) || false,
            result: LatestOffers.getResultData(props.initialData),
        };
    }

    public componentDidMount(): void {
        if (this.panels && this.panels.length > 0) {
            return;
        }

        this.ajaxRequest();
    }

    public render(): JSX.Element {
        const panelsTitle = this.panelsTitles();

        const renderCategoriesCarousel = () => {
            if (!this.state.result?.categoriesCarousel || this.state.isPersonalized === true) {
                return null;
            }

            const categories = this.getCategoriesFromCarousel().filter(
                (category) => panelsTitle.indexOf(category.title || category.name) > -1,
            );

            return (
                <Panel title={this.props.categoriesPanelTitle} titleElement={this.props.panelTitleElement}>
                    <CategoriesCarousel
                        key={`categories-${categories.length}`}
                        {...this.state.result.categoriesCarousel}
                        categories={categories}
                        onClick={LatestOffers.categoryClick}
                        hideSelection={true}
                    />
                </Panel>
            );
        };

        const renderMoreCategories = () => {
            if (!this.state.result?.categoriesCarousel || this.state.isPersonalized === false) {
                return null;
            }

            const categories = this.getCategoriesFromCarousel().filter(
                (category) => panelsTitle.indexOf(category.title || category.name) === -1,
            );

            if (categories.length === 0) {
                return null;
            }

            return (
                <Panel
                    title={this.props.moreCategoriesPanelTitle}
                    titleElement={this.props.panelTitleElement}
                >
                    <CategoriesCarousel
                        {...this.state.result.categoriesCarousel}
                        categories={categories}
                        onClick={this.addCategory}
                        hideSelection={true}
                    />
                </Panel>
            );
        };

        return (
            <div>
                {this.renderAjaxErrorContainer()}
                {renderCategoriesCarousel()}
                {this.renderPanels()}
                {renderMoreCategories()}
                {this.renderLoading()}
            </div>
        );
    }

    protected ajaxSuccess(response: ILatestOffersResult) {
        const result = LatestOffers.getResultData(response);

        this.setState(() => ({
            result,
            isLoading: false,
            isPersonalized: response.isPersonalized,
        }));
    }

    protected ajaxFail() {
        this.setState({ result: {} as ILatestOffersData, isLoading: false });
        return true;
    }

    private getCategoriesFromCarousel = () => this.state.result.categoriesCarousel?.categories || [];

    private renderPanels = () => {
        if (this.state.isLoading === true) {
            return null;
        }

        const panels: JSX.Element[] = [];

        const panelKey = this.state.isPersonalized === true ? `categories-${this.categoriesCounter}` : "0";

        this.panels.forEach((panel, i) => {
            const panelViewState =
                (this.props.componentState?.activePanelState?.title === panel.title &&
                    this.props.componentState.activePanelState) ||
                undefined;

            panel.success = (response) => this.panelLoaded(response || undefined, i);

            panel.fail = () => {
                this.removePanel(i);
                return false;
            };

            panel.hideIfEmpty = true;

            panels.push(
                <DiscountsPanel
                    key={`${panel.title}-${panelKey}-${i}`}
                    disableLazyLoading={false}
                    {...panel}
                    clientId={panel.categoryId}
                    updateViewState={this.updateViewState}
                    componentState={panelViewState}
                    titleElement={this.props.panelTitleElement}
                    hideIfEmpty={false}
                />,
            );
        });

        if (
            !this.state.isPersonalized &&
            this.props.contentContainers &&
            this.props.device !== Device.Mobile &&
            this.props.device !== Device.Tablet
        ) {
            const appContainer = (
                <ContentContainer
                    key="cms-mobile-app-panel-container"
                    {...this.props.contentContainers}
                    onViewTelemetry={{
                        eventName: `${TelemetryEntity.Common}-inTextBanner`,
                        actionType: "View",
                    }}
                    onClickTelemetry={{
                        eventName: `${TelemetryEntity.Common}-inTextBanner`,
                        actionType: "Click",
                    }}
                />
            );

            panels.splice(3, 0, appContainer);
        }

        return <div>{panels}</div>;
    };

    private static getResultData = (data?: ILatestOffersResult): ILatestOffersData => {
        if (!data) {
            return {} as ILatestOffersData;
        }

        return {
            categoriesCarousel: data.categoriesCarousel,
            panels: data.panels.map((value) => ({ ...value, hide: false })),
        };
    };

    private panelsTitles = (state = this.state): string[] => {
        let panelTitles: string[] = [];

        const panels = state.result?.panels || [];

        panels.forEach((panel) => {
            if (state.isPersonalized || panel.hide !== true) {
                panelTitles = [...panelTitles, panel.title];
            }
        });

        return panelTitles;
    };

    private panelLoaded = (response: IDiscountsPanelResult | undefined, index: number) => {
        if (!response || !response.carousels || response.carousels.length === 0) {
            this.removePanel(index);
        }

        const { result } = { ...this.state };

        result.panels[index] = {
            ...result.panels[index],
            initialData: response,
        };

        this.setState(() => ({ result }));
    };

    private removePanel = (index: number) => {
        let panelsUpdated = this.panels;
        panelsUpdated[index].hide = true;

        const panelsToShow = panelsUpdated.filter((value) => value.hide !== true).length;

        if (panelsToShow === 0) {
            if (this.state.isPersonalized === true) {
                panelsUpdated = this.getCategoriesFromCarousel().map(
                    (value) =>
                        ({
                            ...value.categoryPanel,
                            hide: false,
                        }) as IDiscountsPanelExtension,
                );
            } else {
                panelsUpdated.forEach((value) => {
                    value.hide = false;
                });
            }
        }

        this.panels = panelsUpdated;
    };

    private updateViewState = (discountPanelViewState: IDiscountsPanelViewState): boolean | void =>
        this.props.updateViewState
            ? this.props.updateViewState({ activePanelState: discountPanelViewState })
            : true;

    private static categoryClick = (category: ICategoriesCarouselItem) => {
        if (!category) {
            return undefined;
        }

        const panel = document.getElementById(category.id);

        if (panel) {
            jumpToSectionElement(panel);
        }

        return true;
    };

    private addCategory = (category: ICategoriesCarouselItem) => {
        if (!category || !category.categoryPanel) {
            return;
        }

        const result = { ...this.state.result };
        result.panels = [...this.panels, category.categoryPanel];

        this.setState(
            () => ({ result: { ...result } }),
            () => LatestOffers.categoryClick(category),
        );
    };
}

const LatestOffersConnect = (props: ILatestOffers) => {
    const { device } = useViewportInfoContext();

    return <LatestOffers {...props} device={device} />;
};

export { LatestOffersConnect as LatestOffers };
